import React, { Fragment, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { useAlert } from 'react-alert';

import { getDiscountDetails, updateDiscount } from '../../../actions/discountActions';
import { UPDATE_DISCOUNT_RESET } from '../../../constants/discountConstants';
import Loader from '../../Loader';

const EditDiscount = ({ history, match }) => {
  const dispatch = useDispatch();
  const alert = useAlert();

  const [form, setForm] = useState({
    code: '',
    name: '',
    type: 'percentage',
    value: '',
    validFrom: '',
    validUntil: '',
    usageLimit: '',
    description: '',
    status: true,
  });
  const [usedCount, setUsedCount] = useState(0);

  const discountId = match.params.id;

  const { loading: detailsLoading, discount, error: detailsError } = useSelector((state) => state.discountDetails);
  const { loading: updateLoading, success, error: updateError } = useSelector((state) => state.discountUpdate);

  useEffect(() => {
    dispatch(getDiscountDetails(discountId));
  }, [dispatch, discountId]);

  useEffect(() => {
    if (detailsError) alert.error(detailsError);
    if (updateError) alert.error(updateError);
  }, [detailsError, updateError, alert]);

  useEffect(() => {
    if (discount) {
      setForm({
        code: discount.code || '',
        name: discount.name || '',
        type: discount.type || 'percentage',
        value: discount.value ?? '',
        validFrom: discount.validFrom ? new Date(discount.validFrom).toISOString().slice(0, 10) : '',
        validUntil: discount.validUntil ? new Date(discount.validUntil).toISOString().slice(0, 10) : '',
        usageLimit: discount.usageLimit ?? '',
        description: discount.description || '',
        status: !!discount.status,
      });
      setUsedCount(discount.usedCount || 0);
    }
  }, [discount]);

  useEffect(() => {
    if (success) {
      alert.success('Discount updated successfully');
      dispatch({ type: UPDATE_DISCOUNT_RESET });
      history.push('/admin/discounts');
    }
  }, [success, alert, dispatch, history]);

  const onChange = (e) => {
    const { name, value, type, checked } = e.target;
    setForm((prev) => ({ ...prev, [name]: type === 'checkbox' ? checked : value }));
  };

  const submitHandler = (e) => {
    e.preventDefault();
    const payload = {
      ...form,
      value: Number(form.value),
      usageLimit: form.usageLimit !== '' ? Number(form.usageLimit) : undefined,
    };
    dispatch(updateDiscount(discountId, payload));
  };

  const fmtDate = (d) => {
    if (!d) return '';
    try {
      return new Date(d).toISOString().slice(0, 10);
    } catch (e) {
      return d;
    }
  };

  const isLocked = usedCount > 0; // backend also enforces

  const busy = detailsLoading || updateLoading;

  return (
    <Fragment>
      {busy ? (
        <Loader />
      ) : (
        <div className="content-wrapper">
          <section className="content-header">
            <h1>
              Edit <small>Discount</small>
            </h1>
            <ol className="breadcrumb">
              <li>
                <Link to="/admin">
                  <i className="fa fa-dashboard"></i> Dashboard
                </Link>
              </li>
              <li>
                <Link to="/admin/discounts">
                  <i className="fa fa-tags"></i> Discounts
                </Link>
              </li>
              <li className="active">Edit Discount</li>
            </ol>
          </section>

          <section className="content">
            <div className="box box-default">
              <div className="box-header with-border">
                <h3 className="box-title">Edit Discount</h3>
                {isLocked ? (
                  <p style={{ marginTop: 8, color: '#a94442' }}>
                    This discount has been used (usedCount = {usedCount}). Some fields may be locked by the system.
                  </p>
                ) : null}
              </div>
              <form id="quickForm" onSubmit={submitHandler}>
                <div className="box-body">
                  <div className="form-group row">
                    <div className="col-md-6">
                      <label htmlFor="code">Code / Referral Code</label>
                      <input
                        type="text"
                        name="code"
                        className="form-control"
                        id="code"
                        value={form.code}
                        onChange={onChange}
                        required
                        disabled={isLocked}
                      />
                    </div>
                    <div className="col-md-6">
                      <label htmlFor="name">Name</label>
                      <input type="text" name="name" className="form-control" id="name" value={form.name} onChange={onChange} required />
                    </div>
                  </div>

                  <div className="form-group row">
                    <div className="col-md-12">
                      <label htmlFor="description">Description</label>
                      <textarea
                        name="description"
                        id="description"
                        className="form-control"
                        placeholder="Short description of the discount (required)"
                        value={form.description}
                        onChange={onChange}
                        required
                      />
                    </div>
                  </div>

                  <div className="form-group row">
                    <div className="col-md-4">
                      <label htmlFor="type">Type</label>
                      <select name="type" className="form-control" id="type" value={form.type} onChange={onChange} disabled={isLocked}>
                        <option value="percentage">Percentage</option>
                        <option value="fixed">Fixed</option>
                      </select>
                    </div>
                    <div className="col-md-4">
                      <label htmlFor="value">Value</label>
                      <input type="number" step="0.01" name="value" className="form-control" id="value" value={form.value} onChange={onChange} required />
                    </div>
                    <div className="col-md-4">
                      <label className="form-check-label" htmlFor="status">Active</label>
                      <div className="form-check">
                        <input style={{ marginRight: '5px' }} type="checkbox" className="form-check-input" name="status" id="status" checked={form.status} onChange={onChange} />
                      </div>
                    </div>
                  </div>

                  <div className="form-group row">
                    <div className="col-md-6">
                      <label htmlFor="validFrom">Valid From</label>
                      <input type="date" name="validFrom" className="form-control" id="validFrom" value={fmtDate(form.validFrom)} onChange={onChange} required />
                    </div>
                    <div className="col-md-6">
                      <label htmlFor="validUntil">Valid Until</label>
                      <input type="date" name="validUntil" className="form-control" id="validUntil" value={fmtDate(form.validUntil)} onChange={onChange} required />
                    </div>
                  </div>

                  <div className="form-group row">
                    <div className="col-md-4">
                      <label htmlFor="usageLimit">Usage Limit (optional)</label>
                      <input type="number" name="usageLimit" className="form-control" id="usageLimit" value={form.usageLimit} onChange={onChange} placeholder="Leave empty for unlimited" />
                      <small className="help-block">Leave empty for unlimited usage.</small>
                    </div>
                  </div>
                </div>
                <div className="box-footer">
                  <button type="submit" className="btn btn-primary" disabled={isLocked}>
                    {isLocked ? 'Locked (used)' : 'Submit'}
                  </button>
                </div>
              </form>
            </div>
          </section>
        </div>
      )}
    </Fragment>
  );
};

export default EditDiscount;
